/**
 * Static plan definitions for public landing pages.
 * No session / PocketBase call needed.
 * Keep in sync with the PB `plans` collection if prices change.
 */

export interface StaticPlan {
  id: string;
  name: string;
  price: number;
  currency: string;
  interval: 'month' | 'year';
  description: string;
  features: string[];
  tier: number;
  active: boolean;
  highlight: boolean;
  show_pricing: boolean;
}

interface BasePlan {
  slug: string;
  name: string;
  monthlyPrice: number;
  currency: string;
  description: string;
  features: string[];
  tier: number;
  active: boolean;
  highlight: boolean;
  show_pricing: boolean;
}

function yearly(monthly: number): number {
  return Math.floor(monthly * 12 * 0.85); // 15% off
}

const BASE: BasePlan[] = [
  {
    slug: 'basic',
    name: 'Basic',
    monthlyPrice: 0,
    currency: '$',
    description: 'The real-time 0DTE dashboard, always free.',
    tier: 0,
    active: true,
    highlight: false,
    show_pricing: true,
    features: [
      'SPX, SPY, QQQ 0DTE real-time dashboard',
      'Live Volume & Open Interest, strike by strike',
      'GEX chart with Total GEX overlay',
      'DEX chart with Total DEX overlay',
      'Configurable auto-refresh (5–300s)',
    ],
  },
  {
    slug: 'level1',
    name: 'Level 1',
    monthlyPrice: 39,
    currency: '$',
    description: 'Signal Engine, 0DTE Monitor, and TradingView-exported levels — the full real-time toolkit for SPX/SPY/QQQ/ES/NQ 0DTE trading.',
    tier: 1,
    active: true,
    highlight: true,
    show_pricing: true,
    features: [
      'Everything in Basic, plus:',
      'Composite Bias — live GEX/DEX direction call',
      'Structural Map: Walls, Flip Levels, Volume Nodes',
      'Live Whale Tape — institutional flows $100K+',
      'Advanced Chart Mode — correlate any 2 of Vol/OI/GEX/DEX/CEX/VANX/IV',
      'TradingView Daily Levels — export GEX/DEX/OI to your chart, one click',
      'Enhanced Dashboard (SPX/SPY full suite)',
    ],
  },
  {
    slug: 'level2',
    name: 'Level 2',
    monthlyPrice: 59,
    currency: '$',
    description: 'A premium suite of automated market analysis tools that processes the 10 most liquid tickers with real-time data, combining multi-timeframe structural data with comparative AI logic to deliver institutional-grade reports.',
    tier: 2,
    active: true,
    highlight: false,
    show_pricing: true,
    features: [
      'Everything in Level 1, plus:',
      'Top 10 Liquid Tickers — AI deep-dive across the next 4 Weekly Expirations and all-time',
      'Hourly 0DTE AI — real-time scans every hour from 9:00 AM, plus a 3:30 PM Closing Sprint analysis',
      '3-AI Comparative Consensus — Triangulation reports across Pre-Market, Mid-Day, and Post-Market',
      'Multi-Timeframe Structure — holistic structural analysis across all relevant timeframes',
      'All-in-One Market Levels — see the market\'s hidden pulse: SPX/SPY/ES/QQQ/NQ aligned on one screen, every gamma/volume/OI wall pinned side by side to catch cross-market synchronicity before it plays out',
      'Institutional Tape — dedicated whale-flow terminal, filters lotto noise to surface real block trades and sweeps',
      'Long-Term Exposure Monitor (v5) — OI/Volume/Greeks aggregated across the next 4 weekly expirations plus all-time',
    ],
  },
];

export const STATIC_PLANS: StaticPlan[] = BASE.flatMap((p) => [
  {
    id: `${p.slug}-month`,
    name: p.name,
    price: p.monthlyPrice,
    currency: p.currency,
    interval: 'month',
    description: p.description,
    features: p.features,
    tier: p.tier,
    active: p.active,
    highlight: p.highlight,
    show_pricing: p.show_pricing,
  },
  {
    id: `${p.slug}-year`,
    name: p.name,
    price: p.monthlyPrice === 0 ? 0 : yearly(p.monthlyPrice),
    currency: p.currency,
    interval: 'year',
    description: p.description,
    features: p.features,
    tier: p.tier,
    active: p.active,
    highlight: p.highlight,
    show_pricing: p.show_pricing,
  },
]);
